/**
 * `/g/:entryId` — the page a gallery card opens (PLAN-GALLERY G3, revised).
 *
 * A sibling of `/s/:token` and deliberately not the same route: a share link
 * addresses a *token* and shows the recipe as it stands; a gallery entry
 * addresses a *listing* and shows the version its owner published. One URL for
 * both was what made a card quote numbers the page behind it contradicted (see
 * `migrations/0023_gallery_pinned.sql`).
 *
 * Rendered without the app shell for the same reason the share page is: whoever
 * followed a link into a public directory has no workspace on screen and no
 * reason to be offered a sidebar. The clone path needs the router for the same
 * reason too — navigating rather than reloading keeps a fresh clone ahead of the
 * workspace's debounced write to IndexedDB.
 *
 * ## `?from=` is where the card was opened from
 *
 * Because the page renders outside the shell, following a card is a full
 * document load: nothing of the grid behind it survives, and the back link used
 * to land on the bare directory however the brewer had filtered it
 * (PLAN-UX-PASS E3). The gallery's own view rides along as one param the card
 * writes and this route reads back. On the address rather than in
 * `document.referrer`, which is the other carrier available here: the param
 * survives a reload of this page, a restored tab and a pasted link, and it is
 * the same value whether the card was clicked, ⌘-clicked or opened in a new
 * window. Absent means the plain gallery.
 */

import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PublishedRecipePage } from '@/components/gallery/published-recipe';
import { parseSearchString } from '@/lib/search-params';

/** What `/g/:entryId` reads out of its own URL: the view to return to, if any. */
export interface PublishedRecipeSearch {
  readonly from: string | undefined;
}

export const Route = createFileRoute('/g/$entryId')({
  validateSearch: (
    search: { readonly from?: unknown } & SearchSchemaInput,
  ): PublishedRecipeSearch => ({
    // `undefined`, never `null`: a shared link to this page should carry the
    // entry and nothing else (`lib/search-params.ts`).
    from: parseSearchString(search.from),
  }),
  component: PublishedRecipeRoute,
});

function PublishedRecipeRoute() {
  const { entryId } = Route.useParams();
  const { from } = Route.useSearch();
  const navigate = useNavigate();
  return (
    <PublishedRecipePage
      entryId={entryId}
      from={from}
      onOpenRecipe={(recipeId) => {
        void navigate({ to: '/recipes/$recipeId', params: { recipeId } });
      }}
    />
  );
}
